import { createStore } from 'zustand/vanilla'
import { devtools } from 'zustand/middleware'
import type { MosaicBranch, MosaicNode } from 'react-mosaic-component'
import { createRemoveUpdate, updateTree } from 'react-mosaic-component'
import { MosaicNodeSchema, WindowConfigSchema } from '@/types/schemas/workspace'
import {
  DEFAULT_GRID_COLS,
  DEFAULT_LEAF_H,
  mosaicToGrid,
  gridToMosaic,
  reconcileGridCells,
} from '../grid/mosaic-to-grid'
import type {
  WorkspaceDocument,
  TabGroup,
  ComponentType,
  LayoutMode,
  GridCell,
} from '@/types/schemas/workspace'
import { validatePanelState } from '@/types/schemas/panel-state'
import {
  getWorkspaceDoc,
  setWorkspaceDoc,
  backupWorkspaceDoc,
  migrateFromLocalStorage,
} from '@/workspace/idb-cache'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import {
  ConflictError,
  type BootstrapData,
  type PanelState,
  type TemplateData,
  type WindowConfig,
} from '@/workspace/types'
import * as layoutApi from '@/workspace/layout-api'
import { waitForPendingCreate } from '@/workspace/pending-creates'
import { isOfflineError } from '@/workspace/offline-detect'
import type { ErrorPolicy } from '@/workspace/serial-queue'
import {
  isRevisionedLayoutChange,
  mosaicNodesEqual,
  type LayoutSnapshot,
} from '@/workspace/layout-policy'
import { SerialQueue } from '@/workspace/serial-queue'
import { seedSymbolIndex } from '@/workspace/components/link-targets'
import { isSessionEnding } from '@/services/auth/session-ending'

// ─── Validation helpers (exported for testing) ───

export function collectLeaves(node: unknown): string[] {
  if (typeof node === 'string') return [node]
  if (node && typeof node === 'object' && 'first' in node && 'second' in node) {
    const branch = node as { first: unknown; second: unknown }
    return [...collectLeaves(branch.first), ...collectLeaves(branch.second)]
  }
  return []
}

/**
 * True when `key` is `panelId` itself or a synthetic sub-panel minted under it.
 *
 * ChartGrid addresses its nested surfaces by suffixing the panel id: grid cells
 * are `panelId::i`, the drill child is `panelId::drilled`, and the drill's own
 * cells are `panelId::drilled::i` (it mounts itself recursively, so the suffix
 * composes). Those ids never get their own windowConfig, which is why removal
 * has to find them by prefix rather than by lookup.
 *
 * The `::` in the prefix is load-bearing: a bare startsWith(panelId) would also
 * match the sibling `panel-10` while removing `panel-1`.
 */
export function isOwnedPanelKey(key: string, panelId: string): boolean {
  return key === panelId || key.startsWith(`${panelId}::`)
}

/**
 * Copy of `map` without `panelId` and its sub-panel keys. Returns the SAME
 * reference when nothing matched, so a cleanup with nothing to do doesn't
 * re-render every panel subscribed to the map.
 */
export function dropPanelSubtree<T>(map: Record<string, T>, panelId: string): Record<string, T> {
  let dropped = false
  const next: Record<string, T> = {}
  for (const [key, value] of Object.entries(map)) {
    if (isOwnedPanelKey(key, panelId)) {
      dropped = true
      continue
    }
    next[key] = value
  }
  return dropped ? next : map
}

/** Remove one grid cell and let its sole same-row neighbor absorb the vacancy. */
export function removeGridCell(cells: GridCell[], panelId: string): GridCell[] {
  const removed = cells.find((cell) => cell.i === panelId)
  if (!removed) return cells
  const kept = cells.filter((cell) => cell.i !== panelId)
  const adjacent = kept.filter(
    (cell) =>
      cell.y === removed.y &&
      cell.h === removed.h &&
      (cell.x + cell.w === removed.x || removed.x + removed.w === cell.x),
  )
  if (adjacent.length !== 1) return kept
  const neighbor = adjacent[0]
  return kept.map((cell) =>
    cell.i !== neighbor.i
      ? cell
      : neighbor.x < removed.x
        ? { ...neighbor, w: neighbor.w + removed.w }
        : { ...neighbor, x: removed.x, w: neighbor.w + removed.w },
  )
}

/** Mint a new panel ID. Mirrors generatePanelId() in workspace-mosaic.tsx. */
function newPanelId(): string {
  return `pnl_${crypto.randomUUID()}`
}

/** Return a copy of the mosaic tree with every `oldId` leaf renamed to `newId`. */
export function replaceLeafId(
  node: MosaicNode<string> | null,
  oldId: string,
  newId: string,
): MosaicNode<string> | null {
  if (node == null) return node
  if (typeof node === 'string') return node === oldId ? newId : node
  return {
    ...node,
    first: replaceLeafId(node.first, oldId, newId) as MosaicNode<string>,
    second: replaceLeafId(node.second, oldId, newId) as MosaicNode<string>,
  }
}

/** Path to the `id` leaf in the mosaic tree, or null when it isn't a leaf. */
function findLeafPathIn(node: MosaicNode<string> | null, id: string): MosaicBranch[] | null {
  if (node == null) return null
  if (typeof node === 'string') return node === id ? [] : null
  const first = findLeafPathIn(node.first, id)
  if (first) return ['first', ...first]
  const second = findLeafPathIn(node.second, id)
  return second ? ['second', ...second] : null
}

const DEFAULT_WINDOW_CONFIG: WindowConfig = {
  symbolIndex: 0,
  componentType: 'chart',
}

interface LayoutValidationResult {
  mosaicTree: MosaicNode<string> | null
  windowConfigs: Record<string, WindowConfig>
  globalSettings: Record<string, unknown>
  /**
   * Tab groups with a LIVE host (a tree leaf, or a grid cell in grid mode).
   * Groups whose host panel no longer exists are dropped here — historic
   * replace/move bugs orphaned groups in persisted docs, and the orphans are
   * self-preserving (the release-guard treats every group member as a valid
   * panel id, so their dead configs are never pruned). Callers must hydrate
   * THESE, not the raw doc tabGroups, so old docs heal on load.
   */
  tabGroups: Record<string, TabGroup>
  recoveryCount: number
}

export function validateLayoutData(
  tree: unknown,
  configs: Record<string, unknown>,
  gs: Record<string, unknown>,
  tabGroups?: Record<string, TabGroup>,
  gridLayout?: GridCell[],
  /**
   * Grid (scroll) layouts have no mosaic tree — pass true so a null/absent tree
   * validates (mosaicTree stays null) and configs are kept against the grid
   * cells instead of tree leaves. Mosaic layouts leave this false: a null tree
   * still fails, preserving the existing "fall back to defaults" behavior.
   */
  allowNullTree = false,
): LayoutValidationResult | null {
  let mosaicTree: MosaicNode<string> | null = null
  if (tree != null) {
    const treeResult = MosaicNodeSchema.safeParse(tree)
    if (!treeResult.success) return null
    mosaicTree = treeResult.data as MosaicNode<string>
  } else if (!allowNullTree) {
    return null
  }

  const leafIds = new Set(collectLeaves(mosaicTree))

  // Live panel hosts: tree leaves, plus grid cells in grid mode (grid-added
  // panels aren't mosaic leaves; their windowConfigs must survive validation,
  // or switching to grid after a reload would render empty cells).
  const hostIds = new Set(leafIds)
  if (gridLayout) {
    for (const cell of gridLayout) hostIds.add(cell.i)
  }

  // Sanitize tab groups: a group is only valid while its host panel is live.
  // Historic replace/move bugs swapped a group's host out of the tree without
  // deleting the group, leaving self-preserving orphans in persisted docs
  // (the orphan vouches for its members below, so nothing ever pruned them).
  // Dropping dead groups here heals those docs on load.
  const liveTabGroups: Record<string, TabGroup> = {}
  let recoveryCount = 0
  if (tabGroups) {
    for (const [hostId, group] of Object.entries(tabGroups)) {
      if (hostIds.has(hostId)) liveTabGroups[hostId] = group
      else recoveryCount++
    }
  }

  // Tab-member panels are valid panel IDs too — their windowConfigs live in the
  // doc but are NOT mosaic leaves. Without this, every reload would prune them
  // and silently drop the extra tabs in a tab group. Only LIVE groups vouch
  // for members; a dead group's members are pruned with it.
  const validIds = new Set(hostIds)
  for (const group of Object.values(liveTabGroups)) {
    for (const tabId of group.tabs) validIds.add(tabId)
  }

  const repairedConfigs: Record<string, WindowConfig> = {}

  for (const [panelId, config] of Object.entries(configs)) {
    if (!validIds.has(panelId)) continue
    const configResult = WindowConfigSchema.safeParse(config)
    if (configResult.success) {
      repairedConfigs[panelId] = configResult.data
    } else {
      repairedConfigs[panelId] = { ...DEFAULT_WINDOW_CONFIG }
      recoveryCount++
    }
  }

  for (const leafId of leafIds) {
    if (!repairedConfigs[leafId]) {
      repairedConfigs[leafId] = { ...DEFAULT_WINDOW_CONFIG }
      recoveryCount++
    }
  }

  return {
    mosaicTree,
    windowConfigs: repairedConfigs,
    globalSettings: gs ?? {},
    tabGroups: liveTabGroups,
    recoveryCount,
  }
}

export function assembleDocument(
  state: {
    mosaicTree: MosaicNode<string> | null
    windowConfigs: Record<string, WindowConfig>
    globalSettings: Record<string, unknown>
    panels: Record<string, PanelState | null>
    tabGroups?: Record<string, TabGroup>
    layoutMode?: LayoutMode
    gridLayout?: GridCell[]
  },
  layoutRevision?: number,
): WorkspaceDocument {
  // Persist state only for live panels or their `base::sfx` sub-panels (the
  // loader applies the same rule). Removed panels' chart-cell children, stale
  // ids from before cleanupPanel, and the mobile workspace's synthetic ids
  // otherwise ride along in every save until the next reload prunes them.
  const panels: Record<string, Record<string, unknown>> = {}
  for (const [id, s] of Object.entries(state.panels)) {
    if (!s) continue
    const baseId = id.replace(/::.*$/, '')
    if (!state.windowConfigs[id] && !state.windowConfigs[baseId]) continue
    panels[id] = s
  }
  // Persist BOTH structures, always. `layoutMode` picks the active renderer;
  // the inactive structure rides along as a disaster-recovery fallback (a few
  // hundred bytes of coordinates). This is deliberate data-safety design after
  // real losses: a wrong-mode hydrate (stale server doc, wake refetch) can then
  // HIDE the grid but never destroy it. Never null the tree or drop gridLayout
  // here. A never-converted mosaic layout stays byte-identical to legacy docs
  // (no layoutMode/gridLayout keys).
  return {
    version: 2,
    mosaicTree: state.mosaicTree,
    windowConfigs: state.windowConfigs,
    globalSettings: state.globalSettings,
    panels,
    ...(state.tabGroups && Object.keys(state.tabGroups).length > 0
      ? { tabGroups: state.tabGroups }
      : {}),
    ...(state.layoutMode === 'grid' ? { layoutMode: 'grid' as const } : {}),
    ...(state.gridLayout && state.gridLayout.length > 0 ? { gridLayout: state.gridLayout } : {}),
    layoutRevision,
  }
}

export function filterNullPanels(
  panels: Record<string, PanelState | null>,
): Record<string, PanelState> {
  const out: Record<string, PanelState> = {}
  for (const [id, s] of Object.entries(panels)) {
    if (s) out[id] = s
  }
  return out
}

// ─── Store State ───

export interface LayoutStoreState {
  // Layout
  mosaicTree: MosaicNode<string> | null
  windowConfigs: Record<string, WindowConfig>
  globalSettings: Record<string, unknown>
  layoutLoaded: boolean
  recoveryCount: number

  /**
   * Which renderer this layout uses. 'mosaic' = the tiling window manager
   * (default, dashboard); 'grid' = the vertically-scrollable react-grid-layout
   * renderer. Per-layout. `mosaicTree` and `gridLayout` coexist — switching
   * mode never destroys the other structure.
   */
  layoutMode: LayoutMode
  /** react-grid-layout cell arrangement, used only in 'grid' mode. */
  gridLayout: GridCell[]

  // Panels
  panels: Record<string, PanelState | null>
  panelLoaded: Record<string, boolean>

  /**
   * Pane tab-groups, keyed by host mosaic-leaf panel ID. A leaf with no entry
   * (or a single-tab entry) is a plain single-component pane — today's default.
   * See TabGroupSchema. Empty `{}` until panes gain extra tabs.
   */
  tabGroups: Record<string, TabGroup>

  // Template key-remount
  templateKeys: Record<string, number>

  /**
   * Set true when a layout save fails non-recoverably (conflict / generic
   * server error). Optimistic state still applies but server calls are
   * skipped and the staleMessage is re-shown on every attempted edit.
   */
  isStale: boolean
  /** Message to surface when stale. Picked at markStale time based on the
   * error class so subsequent re-shows know which text to use. Empty
   * string when !isStale. */
  staleMessage: string

  // Actions
  updateLayout: (
    tree: MosaicNode<string> | null,
    configs: Record<string, WindowConfig>,
    gs?: Record<string, unknown>,
  ) => void
  updateWindowConfig: (panelId: string, partial: Partial<WindowConfig>) => void
  /**
   * Switch the active renderer. Non-destructive: flipping to 'grid' leaves
   * `mosaicTree` intact and vice-versa. On the first switch to 'grid' with an
   * empty `gridLayout`, the caller (or a follow-up action) seeds it — the store
   * itself only flips the flag and persists.
   */
  setLayoutMode: (mode: LayoutMode) => void
  /**
   * Deliberate one-shot conversion between layout styles. Regenerates the target
   * structure from the current one (mosaic→grid via mosaicToGrid, grid→mosaic
   * via gridToMosaic — lossy but intentional) and DROPS the source, so only one
   * structure is ever stored. Panels + their state are preserved. No-op if
   * already in the target mode.
   */
  convertLayoutMode: (target: LayoutMode) => void
  /** Replace the grid arrangement (drag/resize/add). Marks dirty → persists. */
  setGridLayout: (cells: GridCell[]) => void
  /**
   * Add a panel to a grid (scroll) layout: mints an id, seeds its windowConfig,
   * and appends a full-width cell at the bottom. Does NOT touch the mosaic tree.
   * Returns the new panel id.
   */
  addGridPanel: (componentType: ComponentType, initialState?: Record<string, unknown>) => string
  /**
   * Remove a grid-only panel (not a mosaic leaf): drops its windowConfig, panel
   * state, and grid cell without rebuilding the mosaic tree.
   */
  removeGridPanel: (panelId: string) => void
  registerPanel: (panelId: string) => void
  updatePanel: (panelId: string, partial: Partial<PanelState>) => void
  cleanupPanel: (panelId: string) => void
  loadTemplate: (panelId: string, template: TemplateData) => void

  /**
   * Append a new component as a tab in the pane hosted by `hostId` (a mosaic
   * leaf). Creates the tab group on first add (seeding it with the host as
   * `tabs[0]`), registers the new panel's windowConfig, and makes it active.
   * Returns the new tab's panel ID.
   */
  addTab: (hostId: string, componentType: ComponentType, symbolIndex?: number) => string
  /** Switch the active tab within a pane's tab group. */
  setActiveTab: (hostId: string, tabId: string) => void
  /**
   * Remove a tab from a pane's group, cleaning up its windowConfig + panel
   * state. Removing the last extra tab collapses the group back to a plain
   * single-component pane. Does NOT remove the host leaf from the mosaic tree.
   */
  closeTab: (hostId: string, tabId: string) => void
  /** Reorder the tabs within a pane's group. */
  reorderTabs: (hostId: string, orderedTabIds: string[]) => void
  /**
   * Remove a tab from a group WITHOUT deleting its windowConfig/panel state —
   * used when MOVING a tab elsewhere (caller re-homes it via the tree or
   * attachExistingTab). Collapses the group and reassigns active like closeTab.
   */
  detachTab: (hostId: string, tabId: string) => void
  /**
   * Take `panelId` out of the pane hosted by `hostId` WITHOUT deleting its
   * windowConfig/panel state, so a Move can re-home it. Generalizes detachTab
   * to the pane's host leaf:
   *   - extra tab            → same as detachTab; returns `hostId`.
   *   - host tab of a group  → the next tab is promoted to the tree leaf and
   *                            the group re-keyed under it; returns that id.
   *   - single-panel pane    → the leaf leaves the tree; returns null.
   * The only pane in the layout can't be detached (no-op, returns `hostId`).
   * The return value is the source pane's host id after the detach.
   */
  detachPanel: (hostId: string, panelId: string) => string | null
  /**
   * Add an EXISTING panel (already in windowConfigs) as a tab of the pane
   * hosted by `hostId`, seeding the group with the host on first add and
   * making it active. The companion to detachTab for moving a tab into a group.
   */
  attachExistingTab: (hostId: string, tabId: string) => void
  /**
   * Replace the pane's currently ACTIVE tab with a fresh panel of
   * `componentType`, preserving every other tab. The replaced tab always gets a
   * new panel ID (clean remount), so:
   *   - extra tab active        → swap it in `tabs[]`; the mosaic tree is untouched.
   *   - host-leaf tab active    → swap the leaf in the tree AND re-key the group
   *                               (the group is keyed by the host leaf ID).
   *   - no tab group (single pane) → swap the leaf, same as before.
   * Returns the replaced (old) panel ID so callers can clean up panel-scoped
   * refs they hold outside the store, or null if the host doesn't exist.
   */
  replaceActiveTab: (
    hostId: string,
    componentType: ComponentType,
    initialState?: Record<string, unknown>,
  ) => string | null
  /**
   * Replace the pane's currently ACTIVE tab with an EXISTING panel (already in
   * windowConfigs, e.g. a tab just detached for a move), preserving every
   * other tab. Same branch semantics as replaceActiveTab (tree-leaf swap +
   * group re-key when the host tab is active), but keeps the moved panel's id,
   * config and state instead of minting a new panel. The replaced tab's data
   * is cleaned up. Returns the replaced (old) panel id, or null on no-op
   * (missing panel/host, or the panel is already the active tab).
   */
  replaceActiveTabWithExisting: (hostId: string, panelId: string) => string | null
}

// ─── Store Config ───

export interface LayoutStoreConfig {
  workspaceId: string
  layoutId: string
  /**
   * The workspace scope used by the API namespace
   * (`/v2/workspace/{scope}/...`). Defaults to `workspaceId` when omitted —
   * the dashboard and history workspaces use their own `workspaceId` as
   * scope, while the research layout uses a static workspaceId but the
   * 'dashboard' scope so drawings/templates are shared with the dashboard.
   */
  scope?: string
  defaults: {
    mosaicTree: MosaicNode<string> | null
    windowConfigs: Record<string, WindowConfig>
    tabGroups?: Record<string, TabGroup>
    /** Default renderer for a fresh layout (e.g. a grid preset). Absent = 'mosaic'. */
    layoutMode?: LayoutMode
    /** Default grid arrangement for a grid preset. Absent = empty. */
    gridLayout?: GridCell[]
    /**
     * Grid preset whose tiles never drag or resize (PresetLayout.gridFixed).
     * The user cannot size its cells, so a saved grid's geometry is never
     * restored over the preset's: a changed preset always shows.
     */
    gridFixed?: boolean
    /**
     * Default per-panel states (e.g. from a preset JSON). Seeds the initial
     * `panels` map; persisted (IDB) panel states are merged ON TOP per key,
     * so user tweaks win but preset defaults fill any gaps.
     */
    panels?: Record<string, PanelState>
  }
  /**
   * When true, persist panel states to IDB but skip all API calls.
   * Layout structure (mosaic tree, window configs) is never saved to the API.
   * Used for preset layouts where structure is static but user view settings
   * (timeframe, filters, sort) should survive across sessions.
   */
  localOnly?: boolean
  /**
   * When true, the layout's SHAPE (mosaic tree, window configs, tab groups,
   * grid cells) always comes from `defaults`, never from a saved doc; only
   * panel state and global settings are restored. Unlike `localOnly`, the
   * layout still saves to the server like any other layout, so the user's
   * panel settings follow them across devices while a change to the preset in
   * code reaches everyone. Used for the fixed History presets. Implied by
   * `localOnly`. Saves are never revisioned: the shape cannot change.
   */
  fixedShape?: boolean
  /**
   * Optional promise from the workspace controller's bootstrap fetch.
   * When provided AND the resolved bootstrap covers this layoutId, the
   * store skips its own loadLayout/loadTemplates calls — IDB has the
   * authoritative content.
   *
   * On layout switch (controller's bootstrap finished long ago and was
   * for a different layoutId), this is null and the store falls back
   * to its loadLayout fetch.
   */
  bootstrapPromise?: Promise<BootstrapData | null> | null
  /**
   * Safety-net timeout (ms) for the held loading screen (see init()'s
   * holdForServer path). Defaults to LOAD_RECONCILE_TIMEOUT_MS, or
   * FIXED_SHAPE_REVEAL_GRACE_MS for a fixed-shape layout. Injectable so
   * tests can exercise the timeout-degradation backstop without a real wait.
   */
  reconcileTimeoutMs?: number
  /**
   * Read the current display name for this layout — typically wired to the
   * workspace-store's `treeItems[layoutId]?.name`. Every save embeds the
   * returned value into the saved doc so the body keeps up with the tree.
   * Returning null/undefined skips the field; the older "no name" shape is
   * still accepted by the schema.
   */
  getLayoutName?: () => string | null | undefined
}

// ─── Factory ───
//
// ## Three-stage hydration model
//
// Every layout load follows a deterministic three-stage pipeline:
//
//   Stage 1 — Zustand defaults (synchronous)
//     The store initializes with the static defaults from LayoutStoreConfig.
//     This gives React a valid tree to render immediately (no loading spinner).
//
//   Stage 2 — IndexedDB cache (fast async, <10ms typical)
//     init() reads the IDB doc for this workspaceId+layoutId. If found, the
//     store is hydrated with the cached layout (mosaic tree, window configs,
//     panel states). `layoutLoaded` is set to true at the end of this stage.
//     This is the "warm start" path — users see their last-known layout almost
//     instantly, even if the network is slow or offline.
//
//   Stage 3 — API background fetch (async, network-dependent)
//     A loadLayout() call runs in the background. When the response arrives:
//     - If the user has already made local mutations (dirty / queue busy),
//       the API response is discarded. Local changes win; the next save
//       will push them to the server.
//     - Otherwise, the store merges the API response using a conservative
//       strategy: structural changes (mosaicTree, componentType) are accepted,
//       but local per-panel view state (symbolIndex, linkMode, tickerCleared)
//       is preserved. Only new panels (not present locally) are added.
//     - Zustand's shallow equality check means that when IDB and API agree
//       (the common case), this setState is a no-op — zero re-renders.
//     - `lastLayoutRevision` is set from the API response, arming the
//       concurrency guard for subsequent writes.
//
// ## Sync subsystems
//
//   Auto-save (enqueueSave → SerialQueue)
//     Every local mutation applies optimistic state synchronously and
//     enqueues a single coalesced save task. The task captures the latest
//     state at execution time and calls `adapter.saveLayout` with the
//     current `lastLayoutRevision`. Only on success do we write IDB and
//     post the cross-tab broadcast. Any failure (including 409 conflicts)
//     marks the store stale and surfaces a "refresh required" notification;
//     further mutations are rejected at the action entry until reload.
//
//   BroadcastChannel (same-browser cross-tab)
//     After a successful API save, a message is posted with the new
//     layoutRevision. Other tabs receive it, re-read IDB, and hydrate.
//     This provides near-instant cross-tab sync without polling.
//
//   Visibility handler (cross-browser/device sync)
//     When a tab regains focus after ≥5s hidden, it fetches the layout
//     from the API. If the server's layoutRevision is newer, the store
//     hydrates. This catches changes from other browsers/devices that
//     BroadcastChannel can't reach. The meta-level equivalent lives in
//     use-named-layouts.ts (checks workspaceRevision for layout list changes).
//
//   Beforeunload flush
//     On tab close/refresh, any pending dirty state is flushed to IDB
//     and fire-and-forget to the API. This minimizes data loss on
//     unexpected tab closures.
//
// ## Concurrency model
//
//   Two independent integer counters guard two scopes:
//   - `layoutRevision` (per layout doc): guards content changes (panels,
//     mosaic tree, window configs). Checked in DDB TransactWriteItems.
//   - `workspaceRevision` (on meta record): guards structural changes (layout
//     list: add/remove/rename/reorder). Managed in use-named-layouts.ts.
//
//   Both follow the same pattern: read the current revision, send it with
//   the write request, server rejects with 409 if it doesn't match. On
//   conflict, re-fetch authoritative state and merge.

/**
 * Dev-only knob: delay outgoing `layout:updated` broadcasts by a fixed
 * window so the sender's tab is silent for long enough to let a sibling
 * tab make a divergent edit. Resolves immediately when off (returns false)
 * or after 5s when armed (returns true). Same localStorage key as the
 * controller's `workspace:updated` delay.
 *
 *   localStorage.setItem('edge-dev-broadcast-delay', '1')
 *   localStorage.removeItem('edge-dev-broadcast-delay')
 *
 * Stripped in prod via `import.meta.env.PROD`.
 */
const BROADCAST_DELAY_MS = 5000

// Safety net for the "hold the loading screen until the server layout arrives"
// path (see init()). If Stage 3 reconciliation never completes (bootstrap/fetch
// hangs, no network-layer timeout fires), lift the gate anyway after this bound
// so the user is never stuck on an indefinite loading screen — worst case they
// see the placeholder, same as before this fix. Deliberately generous: a real
// (even slow-3G) layout fetch is a few KB and settles in well under this, so the
// timeout never preempts a working-but-slow load and re-introduces the flash.
const LOAD_RECONCILE_TIMEOUT_MS = 15000

/**
 * The hold for a FIXED-SHAPE layout (a History preset) with nothing cached. Its
 * shape is in code, so there is always something to show: wait this long for
 * the saved settings (the usual case then paints once, already right), then
 * show the preset with its defaults rather than a loading screen. Settings that
 * arrive later fill the panels the user has not touched (applyFixedPayload).
 */
export const FIXED_SHAPE_REVEAL_GRACE_MS = 1000
async function shouldDelayBroadcast(): Promise<boolean> {
  try {
    if (import.meta.env.PROD || import.meta.env.VITEST) return false
    if (typeof localStorage === 'undefined') return false
    if (localStorage.getItem('edge-dev-broadcast-delay') !== '1') return false
    await new Promise((r) => setTimeout(r, BROADCAST_DELAY_MS))
    return true
  } catch {
    //
  }
  return false
}

/**
 * Dev-only broadcast tracing for per-layout content channels. Mirrors the
 * controller's [bcast:ws] logger. Default-on in dev. Silence with:
 *   localStorage.setItem('edge-debug-broadcast', '0')
 */
function bcastLog(...args: unknown[]): void {
  if (import.meta.env.PROD) return
  if (import.meta.env.MODE === 'test') return
  if (typeof localStorage !== 'undefined' && localStorage.getItem('edge-debug-broadcast') === '0')
    return

  console.log('[bcast:layout]', ...args)
}

export function createLayoutStore(config: LayoutStoreConfig) {
  const { workspaceId, layoutId, defaults, localOnly = false } = config
  const fixedShape = localOnly || config.fixedShape === true
  const scope = config.scope ?? workspaceId

  // ─── Closure-scoped sync state ───
  //
  // Mutations apply optimistic state to the in-memory store synchronously and
  // enqueue a single coalesced save task on the serial queue. The task body
  // captures the latest state at execution time, calls `adapter.saveLayout`,
  // and only on success writes IDB and broadcasts cross-tab. Any failure
  // marks the store stale and surfaces a refresh notification.
  //
  //   `dirty`      — there are unsaved changes in the store
  //   `saveQueued` — a save task is in the queue (in-flight or pending)
  //
  // Coalescing rule: while saveQueued is true, additional updates only flip
  // `dirty` true; the existing task will pick up the latest state at run.
  let dirty = false
  /**
   * Fixed shape revealed on the grace timeout, before the server answered:
   * saves wait (so defaults never overwrite the saved settings), and the
   * panel / global state shown at reveal, to tell what the user changed since.
   */
  let awaitingServer = false
  let earlyRevealBaseline: {
    panels: Record<string, unknown>
    globalSettings: Record<string, unknown>
  } | null = null
  /**
   * Re-check timer for the revision-deferral branch below. MUST be tracked so
   * destroy() can clear it: the callback re-enters enqueueSave, which re-defers
   * while lastLayoutRevision is still null, so an untracked timer keeps a
   * destroyed store spinning at 10Hz for the life of the tab. That became
   * reachable once `dirty` stopped being cleared before the flush await, since
   * a destroyed store whose final flush failed keeps dirty set.
   */
  let deferRetryTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Set by destroy(). `init()` is fired unawaited and suspends on the IDB read
   * before it creates the BroadcastChannel and registers the visibilitychange
   * listener, so a destroy() that lands during that window has nothing to
   * remove, and init would then register both against a store that will never
   * be torn down again. Reachable on fast layout switching (`key={layoutId}`
   * remounts per layout), StrictMode double-mount, and discarded renders.
   */
  let destroyed = false
  let saveQueued = false
  let lastLayoutRevision: number | undefined
  let lastHiddenAt = 0
  let broadcastChannel: BroadcastChannel | null = null
  // Fallback timer for the held loading screen (init()'s holdForBootstrap path).
  // Cleared once reconciliation lifts the gate, or on destroy().
  let reconcileLiftTimer: ReturnType<typeof setTimeout> | undefined
  // Last successfully-persisted layout snapshot. layout-policy compares this
  // to the current state at flush time to decide whether the save should
  // bump layoutRevision and broadcast (revisioned) or be last-writer-wins
  // (non-revisioned). See `isRevisionedLayoutChange` in layout-policy.ts.
  let lastPersistedSnapshot: LayoutSnapshot | null = null
  const saveQueue = new SerialQueue()
  function snapshotOf(state: ReturnType<typeof store.getState>): LayoutSnapshot {
    return {
      mosaicTree: state.mosaicTree,
      windowConfigs: state.windowConfigs,
      panels: state.panels as Record<string, PanelState>,
      globalSettings: state.globalSettings,
      tabGroups: state.tabGroups,
      layoutMode: state.layoutMode,
      gridLayout: state.gridLayout,
    }
  }

  /**
   * Apply a saved doc (server payload or cross-tab reload) to a fixed-shape
   * layout: panel state and global settings only, onto the preset's shape.
   * The saved doc's own tree/configs are ignored. Establishes the persisted
   * baseline, since what was applied is what the server holds.
   */
  function applyFixedPayload(
    data: {
      panels?: Record<string, Record<string, unknown>>
      globalSettings?: Record<string, unknown>
    },
    actionName: string,
  ) {
    if (store.getState().layoutLoaded) {
      void backupWorkspaceDoc(workspaceId, layoutId, assembleDocument(store.getState()))
    }
    let panels = data.panels ?? {}
    let globalSettings = data.globalSettings ?? {}
    // Revealed early on the preset's defaults: the saved settings fill what the
    // user has not touched since, and anything they changed stays theirs (and
    // is saved once reconciliation settles, since `dirty` is still set).
    if (earlyRevealBaseline) {
      const base = earlyRevealBaseline
      const now = store.getState()
      panels = { ...panels }
      for (const [id, state] of Object.entries(now.panels)) {
        if (state != null && state !== base.panels[id])
          panels[id] = state as Record<string, unknown>
      }
      globalSettings = { ...globalSettings }
      for (const [key, value] of Object.entries(now.globalSettings)) {
        if (value !== base.globalSettings[key]) globalSettings[key] = value
      }
      earlyRevealBaseline = null
    }
    hydrateFromDoc({ ...assembleDocument(store.getState()), panels, globalSettings }, actionName)
    lastPersistedSnapshot = snapshotOf(store.getState())
  }

  /**
   * Re-hydrate from an external source (BroadcastChannel or 409 re-fetch)
   * without triggering a flush cycle.
   */
  function hydrateFromExternal(
    data: {
      mosaicTree: unknown
      windowConfigs: Record<string, unknown>
      globalSettings?: Record<string, unknown>
      panels?: Record<string, Record<string, unknown>>
      tabGroups?: Record<string, TabGroup>
      layoutMode?: LayoutMode
      gridLayout?: GridCell[]
      layoutRevision?: number
    },
    newLayoutRevision?: number,
  ) {
    if (fixedShape) {
      // External content replaces local state, same as below: drop pending
      // saves so they don't clobber the freshly-applied baseline.
      dirty = false
      saveQueue.clear()
      if (newLayoutRevision != null) lastLayoutRevision = newLayoutRevision
      applyFixedPayload(data, 'hydrate/external')
      return
    }

    const validated = validateLayoutData(
      data.mosaicTree,
      data.windowConfigs,
      data.globalSettings ?? {},
      data.tabGroups,
      data.gridLayout,
      data.layoutMode === 'grid',
    )
    if (!validated) return

    const panelStates: Record<string, PanelState | null> = {}
    const panelLoadedMap: Record<string, boolean> = {}
    if (data.panels) {
      for (const [panelId, rawState] of Object.entries(data.panels)) {
        // Orphan prune: keep a state only for a live panel or a synthetic
        // sub-panel of one (ChartGrid cells / drill children use `base::sfx`
        // ids that never have their own windowConfig).
        const baseId = panelId.replace(/::.*$/, '')
        const config = validated.windowConfigs[panelId] ?? validated.windowConfigs[baseId]
        if (!config) continue
        const validatedState = validatePanelState(config.componentType, rawState)
        if (validatedState) {
          panelStates[panelId] = validatedState
          panelLoadedMap[panelId] = true
        }
      }
    }

    // Safety net: before external content replaces local state, snapshot the
    // CURRENT local doc to the backup slot — if the payload turns out to be
    // stale/wrong, the pre-overwrite layout stays recoverable.
    if (store.getState().layoutLoaded) {
      void backupWorkspaceDoc(workspaceId, layoutId, assembleDocument(store.getState()))
    }

    // External hydration replaces local state; drop pending saves so they
    // don't clobber the freshly-applied baseline. (Any in-flight save still
    // completes; its onSuccess will compute a no-op diff.)
    dirty = false
    saveQueue.clear()

    if (newLayoutRevision != null) lastLayoutRevision = newLayoutRevision

    // Merge windowConfigs: preserve local per-panel view state (symbolIndex,
    // tickerCleared, linkMode) so cross-tab/cross-browser syncs don't bounce
    // tickers or link colors. Only structural fields (componentType, headerVisible)
    // are accepted from the remote. New panels take hydrated values as-is.
    //
    // Merge panels: existing local panel state is preserved entirely — cross-tab
    // sync only carries structural changes (add/remove panels). This lets each
    // tab diverge on filters, sorting, etc. while still picking up new panels.
    store.setState(
      (s) => {
        const mergedConfigs = { ...validated.windowConfigs }
        for (const [panelId, config] of Object.entries(mergedConfigs)) {
          const local = s.windowConfigs[panelId]
          if (local) {
            mergedConfigs[panelId] = {
              ...config,
              symbolIndex: local.symbolIndex,
              tickerCleared: local.tickerCleared,
              linkMode: local.linkMode,
            }
          }
        }

        // Only hydrate panels that don't exist locally (new panels from another tab)
        const mergedPanels = { ...s.panels }
        const mergedPanelLoaded = { ...s.panelLoaded }
        for (const [panelId, state] of Object.entries(panelStates)) {
          if (!s.panels[panelId]) {
            mergedPanels[panelId] = state
            mergedPanelLoaded[panelId] = true
          }
        }

        return {
          mosaicTree: validated.mosaicTree,
          windowConfigs: mergedConfigs,
          globalSettings: validated.globalSettings ?? s.globalSettings,
          panels: mergedPanels,
          panelLoaded: mergedPanelLoaded,
          tabGroups: validated.tabGroups,
          layoutMode: data.layoutMode ?? 'mosaic',
          gridLayout: data.gridLayout ?? [],
        }
      },
      undefined,
      'hydrate/external',
    )
    // External hydration IS the persisted baseline.
    lastPersistedSnapshot = snapshotOf(store.getState())
  }

  /** Outcome handed from the queue task to onSuccess. */
  type SaveOutcome =
    | { kind: 'noop' }
    | { kind: 'deferred' }
    | { kind: 'localOnly'; doc: WorkspaceDocument }
    | {
        kind: 'api'
        // layoutRevision is only present on revisioned save responses.
        result: { updatedAt: string; layoutRevision?: number } | void
        doc: WorkspaceDocument
        snapshot: LayoutSnapshot
        revisioned: boolean
      }

  const STALE_CONFLICT_MESSAGE =
    'Your workspace is out of sync. Refresh the page to continue saving.'
  const STALE_GENERIC_MESSAGE =
    'There was an issue saving your layout. Please refresh, or try again later.'
  const OFFLINE_MESSAGE =
    "You're offline. Your changes are queued and will save when the connection returns."
  const MAX_OFFLINE_QUEUE = 10

  function pickStaleMessage(err: unknown): string {
    if (err instanceof ConflictError) return STALE_CONFLICT_MESSAGE
    return STALE_GENERIC_MESSAGE
  }

  /** Show a toast. Re-showing replaces the current one — the desired UX
   * is a fresh reminder on every attempted edit. */
  function showToast(message: string, variant: 'error' | 'warning' = 'error'): void {
    void import('@/stores/notification-store').then(({ useNotificationStore }) => {
      if (variant === 'warning') useNotificationStore.getState().showWarning(message)
      else useNotificationStore.getState().showError(message)
    })
  }

  function notifyOffline(): void {
    showToast(OFFLINE_MESSAGE, 'warning')
  }

  function markStale(err: unknown, reason: 'conflict' | 'overflow' | 'generic' = 'generic'): void {
    if (store.getState().isStale) return
    // Forced sign-out in progress (e.g. signed in on another device): the failed save is
    // a symptom, and its toast would replace the real reason. See session-ending.ts.
    if (isSessionEnding()) return
    const message = pickStaleMessage(err)
    console.error('[workspace-store] save failed; marking stale', err)
    // Terminal state: saves have stopped and the user must reload. The
    // reason is the failure's shape, never its text.
    trackEvent(EVENTS.WORKSPACE_MARKED_STALE, {
      workspace_id: workspaceId,
      layout_id: layoutId,
      reason: err instanceof ConflictError ? 'conflict' : reason,
    })
    store.setState({ isStale: true, staleMessage: message }, undefined, 'markStale')
    showToast(message)
  }

  /**
   * Mark the store dirty and enqueue a save task if one isn't already
   * queued. Multiple rapid actions coalesce: only one task is in flight at
   * a time, and it captures the latest state at execution time.
   */
  function enqueueSave() {
    const s = store.getState()
    // CRITICAL data-loss guard: never persist before the layout has hydrated.
    // Until init() reads IDB and hydrateFromDoc runs, the store holds DEFAULT
    // state (default 'mosaic' mode, empty gridLayout). A save in that window
    // would persist the defaults — and since a doc only stores its ACTIVE
    // structure, a mosaic-default save DROPS the real gridLayout and resets a
    // scroll layout. `layoutLoaded` flips true right after hydration, so gating
    // here closes the window (incl. remounts from HMR). A brand-new layout has
    // no IDB doc, so layoutLoaded flips true immediately and its defaults ARE
    // the content — legit saves still flow.
    if (!s.layoutLoaded) return
    // Stale = can't reach the server. Optimistic state still lives in
    // memory (so the user sees their interaction), but we re-show the
    // saved staleMessage on every attempted edit so they know nothing's
    // persisting until they reload.
    if (s.isStale) {
      showToast(s.staleMessage || STALE_CONFLICT_MESSAGE)
      return
    }
    // Cap the offline backlog. If the queue keeps growing through an
    // extended outage, eventually we give up and mark stale instead of
    // retrying forever.
    if (saveQueue.size() >= MAX_OFFLINE_QUEUE) {
      console.error('[workspace-store] save queue exceeded MAX_OFFLINE_QUEUE; marking stale', {
        size: saveQueue.size(),
      })
      trackEvent(EVENTS.WORKSPACE_SAVE_QUEUE_OVERFLOW, {
        workspace_id: workspaceId,
        layout_id: layoutId,
        queue_size: saveQueue.size(),
      })
      markStale(new Error('Save queue overflow during outage'), 'overflow')
      return
    }
    dirty = true
    if (saveQueued) return
    saveQueued = true

    saveQueue.push<SaveOutcome>(
      async () => {
        // Clear the queued flag at task start so updates that arrive while
        // we're running can enqueue a follow-up save.
        saveQueued = false
        if (!dirty) return { kind: 'noop' }
        // Revealed early and the server has not answered: keep the edit (dirty)
        // for the save that runs once reconciliation settles.
        if (awaitingServer) return { kind: 'deferred' }
        dirty = false

        // A freshly created/duplicated layout must not have its first PUT
        // race the create mutation to the server (the controller's queue
        // and this one are independent). Wait for the creation to settle;
        // immediate for every layout not pending creation. State is
        // captured AFTER the wait so the save carries the latest edits.
        if (!localOnly) await waitForPendingCreate(layoutId)

        const state = store.getState()
        const doc = assembleDocument(state)

        if (localOnly) {
          return { kind: 'localOnly', doc }
        }

        const nextSnapshot = snapshotOf(state)
        // A fixed-shape layout's structure cannot change, so its saves are
        // never revisioned (panel state is last-writer-wins on every layout).
        // This also lets the first save create the server doc: a revisioned
        // save against a doc that does not exist yet is rejected as a conflict.
        const revisioned =
          !fixedShape && isRevisionedLayoutChange(lastPersistedSnapshot, nextSnapshot)

        // INVARIANT: every layout doc carries a layoutRevision from creation
        // onward. If we don't have one locally on a revisioned save, init
        // hasn't fully reconciled yet — saving now would race a stale request
        // through last-writer-wins and silently clobber other tabs. Defer;
        // re-set dirty so a follow-up enqueue picks it up after reconciliation.
        if (revisioned && lastLayoutRevision == null) {
          console.error(
            '[workspace-store] revisioned save attempted with no lastLayoutRevision; ' +
              'deferring until reconciliation completes',
            { workspaceId, layoutId },
          )
          // The last-writer-wins race guard fired. Frequency here points at
          // a reconciliation bug, not a user action.
          trackEvent(EVENTS.WORKSPACE_REVISIONED_SAVE_DEFERRED, {
            workspace_id: workspaceId,
            layout_id: layoutId,
          })
          dirty = true
          // Schedule a re-check shortly — by then init() should have
          // populated lastLayoutRevision. Tracked (and never stacked) so
          // destroy() can stop it.
          if (deferRetryTimer) clearTimeout(deferRetryTimer)
          deferRetryTimer = setTimeout(() => {
            deferRetryTimer = null
            if (dirty) enqueueSave()
          }, 100)
          return { kind: 'deferred' }
        }

        const layoutName = config.getLayoutName?.() ?? undefined
        const result = await layoutApi.saveLayout(
          scope,
          layoutId,
          {
            // Keep-both: persist BOTH structures (see assembleDocument).
            mosaicTree: state.mosaicTree,
            windowConfigs: state.windowConfigs,
            globalSettings: state.globalSettings,
            panels: filterNullPanels(state.panels),
            // ALWAYS send layoutMode + tabGroups, even when 'mosaic' / empty.
            // `save_layout` builds a partial DynamoDB UpdateExpression that SETs
            // only the keys present in the body and issues no REMOVE, so an
            // omitted key keeps its stored value forever. Omitting these two on
            // the clearing edge made grid->mosaic conversion and last-tab-close
            // silently revert on the next bootstrap. See store.unit.test.ts
            // 'always sends layoutMode + tabGroups'.
            tabGroups: state.tabGroups,
            layoutMode: state.layoutMode,
            // gridLayout stays CONDITIONAL on purpose. Keep-both (see
            // assembleDocument) says the inactive structure rides along as a
            // disaster-recovery fallback and is never destroyed, so here
            // omission is the safe direction: it preserves the server's copy.
            // Force-sending [] would delete a fallback we may still need.
            //
            // KNOWN LIMITATION, deliberate: because the backend never issues a
            // REMOVE, there is consequently no way to intentionally CLEAR a
            // stored gridLayout from here. No UI path needs that today. If one
            // ever does, add an explicit clear rather than making this
            // unconditional, which would destroy the fallback on every mosaic
            // save.
            ...(state.gridLayout.length > 0 ? { gridLayout: state.gridLayout } : {}),
            layoutRevision: lastLayoutRevision,
            ...(layoutName != null ? { name: layoutName } : {}),
          },
          { revisioned },
        )
        return { kind: 'api', result, doc, snapshot: nextSnapshot, revisioned }
      },
      {
        onSuccess: async (outcome) => {
          if (outcome.kind === 'noop' || outcome.kind === 'deferred') return

          if (outcome.kind === 'localOnly') {
            await setWorkspaceDoc(workspaceId, layoutId, outcome.doc)
            if (broadcastChannel) {
              bcastLog('post (localOnly)', { layoutId })
              broadcastChannel.postMessage({ type: 'layout:updated' })
            } else {
              bcastLog('SKIP post — no broadcastChannel', { layoutId })
            }
            return
          }

          // api
          // Adopt the server's revision only from REVISIONED responses.
          // Non-revisioned saves never bump the server revision, so any
          // layoutRevision they echo carries no authority; adopting one
          // (the legacy server echoed `expected or 0`) poisons the baseline
          // and 409s the next structural save.
          if (outcome.revisioned && outcome.result?.layoutRevision != null) {
            lastLayoutRevision = outcome.result.layoutRevision
          }
          lastPersistedSnapshot = outcome.snapshot
          // IDB only after server confirms — keeps the cache in lockstep
          // with what the server accepted. Stamp the just-confirmed
          // layoutRevision into the doc so a fresh mount of this layout
          // (after navigation away and back) reads the right baseline
          // and doesn't defer the next save with "no lastLayoutRevision".
          await setWorkspaceDoc(workspaceId, layoutId, {
            ...outcome.doc,
            layoutRevision: lastLayoutRevision,
          })

          if (!outcome.revisioned) {
            // Non-revisioned saves intentionally don't broadcast — other tabs
            // see the change on their next natural reload.
            bcastLog('SKIP post — non-revisioned save', {
              layoutId,
              layoutRevision: lastLayoutRevision,
            })
          } else if (!broadcastChannel) {
            bcastLog('SKIP post — no broadcastChannel', {
              layoutId,
              layoutRevision: lastLayoutRevision,
            })
          } else {
            /* Delay controlled by localstorage, dev-only*/
            const delayed = await shouldDelayBroadcast()

            // The channel may have been closed during the delay (HMR /
            // unmount); broadcastChannel is null after destroy. Re-check.
            if (!broadcastChannel) {
              bcastLog('SKIP post — channel closed during delay', { layoutId })
            } else {
              bcastLog(delayed ? 'post (after delay)' : 'post', {
                layoutId,
                layoutRevision: lastLayoutRevision,
              })
              broadcastChannel.postMessage({
                type: 'layout:updated',
                layoutRevision: lastLayoutRevision,
              })
            }
          }
        },
        onError: (err): ErrorPolicy => {
          if (isOfflineError(err)) {
            // Transient — keep the failed task at the head of the queue.
            // saveQueued was cleared at this task's start, so the user's
            // next edit will push a fresh task, which retriggers drain
            // and re-attempts the head (this stuck task). When the
            // network returns the head succeeds, then the queue continues
            // through the rest. Each task body re-reads current state at
            // run time, so the retried head sends the latest content.
            console.warn('[workspace-store] save failed (offline); queued for retry', err)
            notifyOffline()
            return 'retry'
          }
          markStale(err)
          return 'drop'
        },
      },
    )
  }

  const store = createStore<LayoutStoreState>()(
    devtools(
      (set, get) => ({
        // Initial state — defaults render nothing meaningful.
        // Real data arrives from IDB in init() (~30ms).
        mosaicTree: defaults.mosaicTree,
        windowConfigs: defaults.windowConfigs,
        globalSettings: {},
        layoutLoaded: false,
        recoveryCount: 0,
        layoutMode: defaults.layoutMode ?? 'mosaic',
        gridLayout: defaults.gridLayout ?? [],
        panels: { ...(defaults.panels ?? {}) },
        panelLoaded: {},
        tabGroups: defaults.tabGroups ?? {},
        templateKeys: {},
        isStale: false,
        staleMessage: '',

        // Layout actions — optimistic state always applies. enqueueSave
        // handles the stale gate (skip server, re-show toast).
        updateLayout: (tree, configs, gs) => {
          set(
            (s) => ({
              mosaicTree: tree,
              windowConfigs: configs,
              globalSettings: gs ?? s.globalSettings,
            }),
            undefined,
            'updateLayout',
          )
          enqueueSave()
        },

        updateWindowConfig: (panelId, partial) => {
          const current = get().windowConfigs[panelId]
          if (!current) return
          set(
            (s) => ({
              windowConfigs: { ...s.windowConfigs, [panelId]: { ...current, ...partial } },
            }),
            undefined,
            'updateWindowConfig',
          )
          enqueueSave()
        },

        setLayoutMode: (mode) => {
          if (get().layoutMode === mode) return
          set({ layoutMode: mode }, undefined, 'setLayoutMode')
          enqueueSave()
        },

        convertLayoutMode: (target) => {
          const s = get()
          if (s.layoutMode === target) return
          // Both directions reconcile against the REAL panel set and flatten tab
          // groups. Reconciling drops stale cells (a stale cell would otherwise
          // become a tree leaf that validateLayoutData "repairs" into a phantom
          // default chart) and appends panels that have a config but no cell
          // (e.g. tab members, which are configs but not tree leaves). Grid mode
          // has no tab concept, so conversion flattens each tab into its own
          // standalone panel; carrying tabGroups across would leave a panel
          // simultaneously owned by a group and the layout structure.
          //
          // Keep-both: the SOURCE structure is retained (persisted alongside)
          // as a disaster-recovery fallback — conversion regenerates the target
          // from the current arrangement, it never destroys the other one.
          const panelIds = Object.keys(s.windowConfigs)
          if (target === 'grid') {
            const cells = reconcileGridCells(mosaicToGrid(s.mosaicTree), panelIds)
            set(
              { layoutMode: 'grid', gridLayout: cells, tabGroups: {} },
              undefined,
              'convertLayoutMode/grid',
            )
          } else {
            const cells = reconcileGridCells(s.gridLayout, panelIds)
            set(
              { layoutMode: 'mosaic', mosaicTree: gridToMosaic(cells), tabGroups: {} },
              undefined,
              'convertLayoutMode/mosaic',
            )
          }
          enqueueSave()
        },

        setGridLayout: (cells) => {
          set({ gridLayout: cells }, undefined, 'setGridLayout')
          enqueueSave()
        },

        addGridPanel: (componentType, initialState) => {
          const id = newPanelId()
          set(
            (s) => {
              const bottomY = s.gridLayout.reduce((max, c) => Math.max(max, c.y + c.h), 0)
              return {
                // Seed from the onChange link group's current ticker, not
                // symbols[0] — mirrors insertPanelAtTarget (mosaic mode).
                windowConfigs: {
                  ...s.windowConfigs,
                  [id]: { symbolIndex: seedSymbolIndex(s.windowConfigs), componentType },
                },
                gridLayout: [
                  ...s.gridLayout,
                  { i: id, x: 0, y: bottomY, w: DEFAULT_GRID_COLS, h: DEFAULT_LEAF_H },
                ],
                panelLoaded: { ...s.panelLoaded, [id]: true },
                ...(initialState
                  ? { panels: { ...s.panels, [id]: initialState as PanelState } }
                  : {}),
              }
            },
            undefined,
            'addGridPanel',
          )
          enqueueSave()
          return id
        },

        removeGridPanel: (id) => {
          set(
            (s) => {
              const { [id]: _cfg, ...restConfigs } = s.windowConfigs
              return {
                windowConfigs: restConfigs,
                panels: dropPanelSubtree(s.panels, id),
                panelLoaded: dropPanelSubtree(s.panelLoaded, id),
                templateKeys: dropPanelSubtree(s.templateKeys, id),
                gridLayout: removeGridCell(s.gridLayout, id),
              }
            },
            undefined,
            'removeGridPanel',
          )
          enqueueSave()
        },

        // Panel actions — state is already hydrated from IDB/API during init()
        registerPanel: (panelId) => {
          if (get().panelLoaded[panelId]) return
          set(
            (s) => ({ panelLoaded: { ...s.panelLoaded, [panelId]: true } }),
            undefined,
            'registerPanel',
          )
        },

        updatePanel: (panelId, partial) => {
          set(
            (s) => ({
              panels: { ...s.panels, [panelId]: { ...s.panels[panelId], ...partial } },
            }),
            undefined,
            'updatePanel',
          )
          enqueueSave()
        },

        cleanupPanel: (panelId) => {
          set(
            (s) => ({
              // Sweep the sub-panel keys too. They have no windowConfig of
              // their own, so an exact-key delete stranded a closed panel's
              // cell/drill state in the doc; hydrate's orphan prune only
              // reclaimed it on the next reload.
              panels: dropPanelSubtree(s.panels, panelId),
              panelLoaded: dropPanelSubtree(s.panelLoaded, panelId),
              templateKeys: dropPanelSubtree(s.templateKeys, panelId),
            }),
            undefined,
            'cleanupPanel',
          )
          enqueueSave()
        },

        // Template key-remount. Templates carry indicator/chartType shape
        // only — chart settings profiles use `EdgeChartApi.applySettings`
        // for in-place updates (no remount).
        loadTemplate: (panelId, template) => {
          set(
            (s) => ({
              panels: {
                ...s.panels,
                [panelId]: {
                  ...s.panels[panelId],
                  chartType: template.chartType,
                  mainIndicators: template.mainIndicators,
                  subIndicators: template.subIndicators,
                },
              },
              templateKeys: {
                ...s.templateKeys,
                [panelId]: (s.templateKeys[panelId] ?? 0) + 1,
              },
            }),
            undefined,
            'loadTemplate',
          )
          enqueueSave()
        },

        // ─── Tab groups ───
        // A pane is identified by its mosaic-leaf host ID. The group seeds
        // itself with the host as tabs[0] on first add; "no group" / single-tab
        // means a plain single-component pane (today's default).

        addTab: (hostId, componentType, symbolIndex) => {
          const id = newPanelId()
          set(
            (s) => {
              const hostCfg = s.windowConfigs[hostId]
              const existing = s.tabGroups[hostId] ?? {
                tabs: [hostId],
                activeTabId: hostId,
              }
              const newConfig: WindowConfig = {
                symbolIndex: symbolIndex ?? hostCfg?.symbolIndex ?? 0,
                componentType,
                ...(hostCfg?.linkMode ? { linkMode: hostCfg.linkMode } : {}),
              }
              return {
                windowConfigs: { ...s.windowConfigs, [id]: newConfig },
                tabGroups: {
                  ...s.tabGroups,
                  [hostId]: { tabs: [...existing.tabs, id], activeTabId: id },
                },
              }
            },
            undefined,
            'addTab',
          )
          enqueueSave()
          return id
        },

        setActiveTab: (hostId, tabId) => {
          set(
            (s) => {
              const group = s.tabGroups[hostId]
              if (!group || !group.tabs.includes(tabId) || group.activeTabId === tabId) return s
              return {
                tabGroups: { ...s.tabGroups, [hostId]: { ...group, activeTabId: tabId } },
              }
            },
            undefined,
            'setActiveTab',
          )
          enqueueSave()
        },

        closeTab: (hostId, tabId) => {
          set(
            (s) => {
              const group = s.tabGroups[hostId]
              if (!group || !group.tabs.includes(tabId)) return s

              const remaining = group.tabs.filter((t) => t !== tabId)

              // Clean up the closed tab's panel data — unless it's the host
              // leaf itself, which the mosaic tree still owns.
              const windowConfigs = { ...s.windowConfigs }
              const panels = { ...s.panels }
              const panelLoaded = { ...s.panelLoaded }
              const templateKeys = { ...s.templateKeys }
              if (tabId !== hostId) {
                delete windowConfigs[tabId]
                delete panels[tabId]
                delete panelLoaded[tabId]
                delete templateKeys[tabId]
              }

              const tabGroups = { ...s.tabGroups }
              // 1 remaining tab → collapse back to a plain single-component pane.
              if (remaining.length <= 1) {
                delete tabGroups[hostId]
              } else {
                const activeTabId =
                  group.activeTabId === tabId ? remaining[remaining.length - 1] : group.activeTabId
                tabGroups[hostId] = { tabs: remaining, activeTabId }
              }

              return { windowConfigs, panels, panelLoaded, templateKeys, tabGroups }
            },
            undefined,
            'closeTab',
          )
          enqueueSave()
        },

        reorderTabs: (hostId, orderedTabIds) => {
          set(
            (s) => {
              const group = s.tabGroups[hostId]
              if (!group) return s
              // Guard: the reorder must be a permutation of the current tabs.
              const same =
                orderedTabIds.length === group.tabs.length &&
                orderedTabIds.every((t) => group.tabs.includes(t))
              if (!same) return s
              return {
                tabGroups: { ...s.tabGroups, [hostId]: { ...group, tabs: orderedTabIds } },
              }
            },
            undefined,
            'reorderTabs',
          )
          enqueueSave()
        },

        detachTab: (hostId, tabId) => {
          set(
            (s) => {
              const group = s.tabGroups[hostId]
              if (!group || !group.tabs.includes(tabId)) return s
              const remaining = group.tabs.filter((t) => t !== tabId)
              const tabGroups = { ...s.tabGroups }
              // NOTE: unlike closeTab, we keep windowConfigs/panels for tabId —
              // the caller re-homes it (tree insert or attachExistingTab).
              if (remaining.length <= 1) {
                delete tabGroups[hostId]
              } else {
                const activeTabId =
                  group.activeTabId === tabId ? remaining[remaining.length - 1] : group.activeTabId
                tabGroups[hostId] = { tabs: remaining, activeTabId }
              }
              return { tabGroups }
            },
            undefined,
            'detachTab',
          )
          enqueueSave()
        },

        detachPanel: (hostId, panelId) => {
          const s0 = get()
          const group = s0.tabGroups[hostId]
          if (panelId !== hostId) {
            get().detachTab(hostId, panelId)
            return hostId
          }

          if (group && group.tabs.length >= 2) {
            // Promote the next tab to the tree leaf; the group is keyed by the
            // leaf id, so it moves with it (or collapses at one tab).
            const remaining = group.tabs.filter((t) => t !== hostId)
            const promoted = remaining[0]
            set(
              (s) => {
                const { [hostId]: _g, ...tabGroups } = s.tabGroups
                if (remaining.length >= 2) {
                  const activeTabId =
                    group.activeTabId === hostId || !remaining.includes(group.activeTabId)
                      ? remaining[remaining.length - 1]
                      : group.activeTabId
                  tabGroups[promoted] = { tabs: remaining, activeTabId }
                }
                return {
                  mosaicTree: replaceLeafId(s.mosaicTree, hostId, promoted),
                  tabGroups,
                }
              },
              undefined,
              'detachPanel',
            )
            enqueueSave()
            return promoted
          }

          // Plain pane: remove the leaf from the tree, keep its config/state.
          const path = findLeafPathIn(s0.mosaicTree, hostId)
          if (!path || path.length === 0 || s0.mosaicTree == null) return hostId
          const mosaicTree = updateTree(s0.mosaicTree, [createRemoveUpdate(s0.mosaicTree, path)])
          set(
            (s) => {
              if (!group) return { mosaicTree }
              const { [hostId]: _g, ...tabGroups } = s.tabGroups
              return { mosaicTree, tabGroups }
            },
            undefined,
            'detachPanel',
          )
          enqueueSave()
          return null
        },

        attachExistingTab: (hostId, tabId) => {
          set(
            (s) => {
              // Can only attach a panel that already has a windowConfig.
              if (!s.windowConfigs[tabId]) return s
              const existing = s.tabGroups[hostId] ?? { tabs: [hostId], activeTabId: hostId }
              if (existing.tabs.includes(tabId)) {
                return {
                  tabGroups: { ...s.tabGroups, [hostId]: { ...existing, activeTabId: tabId } },
                }
              }
              return {
                tabGroups: {
                  ...s.tabGroups,
                  [hostId]: { tabs: [...existing.tabs, tabId], activeTabId: tabId },
                },
              }
            },
            undefined,
            'attachExistingTab',
          )
          enqueueSave()
        },

        replaceActiveTab: (hostId, componentType, initialState) => {
          const s0 = get()
          const group = s0.tabGroups[hostId]
          // Nothing to replace if the host pane doesn't exist.
          if (s0.windowConfigs[hostId] == null && group == null) return null

          // The active tab is what the pane currently renders; defaults to the
          // host leaf when there's no group or the active id has gone stale.
          const activeId =
            group && group.tabs.includes(group.activeTabId) ? group.activeTabId : hostId
          const tabs = group?.tabs ?? [hostId]

          const newId = newPanelId()
          const srcCfg = s0.windowConfigs[activeId] ?? s0.windowConfigs[hostId]
          const newConfig: WindowConfig = {
            symbolIndex: srcCfg?.symbolIndex ?? 0,
            componentType,
            ...(srcCfg?.linkMode ? { linkMode: srcCfg.linkMode } : {}),
          }
          const seededState = initialState ? validatePanelState(componentType, initialState) : null

          set(
            (s) => {
              // Drop the replaced tab's config + transient state; add the new one.
              const { [activeId]: _oldCfg, ...restConfigs } = s.windowConfigs
              const { [activeId]: _p, ...panelsRest } = s.panels
              const { [activeId]: _pl, ...panelLoadedRest } = s.panelLoaded
              const { [activeId]: _tk, ...templateKeys } = s.templateKeys
              const windowConfigs = { ...restConfigs, [newId]: newConfig }
              const panels = seededState ? { ...panelsRest, [newId]: seededState } : panelsRest
              const panelLoaded = seededState
                ? { ...panelLoadedRest, [newId]: true }
                : panelLoadedRest
              const base = { windowConfigs, panels, panelLoaded, templateKeys }

              if (activeId === hostId) {
                // Active tab IS the mosaic leaf → swap the leaf in the tree. The
                // group (if any) is keyed by the host leaf id, so re-key it too.
                const mosaicTree = replaceLeafId(s.mosaicTree, hostId, newId)
                if (!group) return { ...base, mosaicTree }
                const { [hostId]: _g, ...restGroups } = s.tabGroups
                return {
                  ...base,
                  mosaicTree,
                  tabGroups: {
                    ...restGroups,
                    [newId]: {
                      tabs: tabs.map((t) => (t === hostId ? newId : t)),
                      activeTabId: newId,
                    },
                  },
                }
              }

              // Active tab is an extra tab → swap it inside the group; the mosaic
              // tree (and the host leaf) are untouched.
              return {
                ...base,
                tabGroups: {
                  ...s.tabGroups,
                  [hostId]: {
                    tabs: tabs.map((t) => (t === activeId ? newId : t)),
                    activeTabId: newId,
                  },
                },
              }
            },
            undefined,
            'replaceActiveTab',
          )
          enqueueSave()
          return activeId
        },

        replaceActiveTabWithExisting: (hostId, panelId) => {
          const s0 = get()
          // The moved panel must already exist (detached from its source group
          // with its windowConfig/state intact).
          if (!s0.windowConfigs[panelId]) return null
          const group = s0.tabGroups[hostId]
          if (s0.windowConfigs[hostId] == null && group == null) return null

          const activeId =
            group && group.tabs.includes(group.activeTabId) ? group.activeTabId : hostId
          if (activeId === panelId) return null
          const tabs = group?.tabs ?? [hostId]

          set(
            (s) => {
              // Drop the replaced tab's config + transient state; the moved
              // panel keeps its own.
              const { [activeId]: _oldCfg, ...windowConfigs } = s.windowConfigs
              const { [activeId]: _p, ...panels } = s.panels
              const { [activeId]: _pl, ...panelLoaded } = s.panelLoaded
              const { [activeId]: _tk, ...templateKeys } = s.templateKeys
              const base = { windowConfigs, panels, panelLoaded, templateKeys }

              if (activeId === hostId) {
                // Active tab IS the mosaic leaf → swap the leaf in the tree and
                // re-key the group (keyed by the host leaf id) if there is one.
                const mosaicTree = replaceLeafId(s.mosaicTree, hostId, panelId)
                if (!group) return { ...base, mosaicTree }
                const { [hostId]: _g, ...restGroups } = s.tabGroups
                return {
                  ...base,
                  mosaicTree,
                  tabGroups: {
                    ...restGroups,
                    [panelId]: {
                      tabs: tabs.map((t) => (t === hostId ? panelId : t)),
                      activeTabId: panelId,
                    },
                  },
                }
              }

              // Active tab is an extra tab → swap it inside the group; the
              // mosaic tree (and the host leaf) are untouched.
              return {
                ...base,
                tabGroups: {
                  ...s.tabGroups,
                  [hostId]: {
                    tabs: tabs.map((t) => (t === activeId ? panelId : t)),
                    activeTabId: panelId,
                  },
                },
              }
            },
            undefined,
            'replaceActiveTabWithExisting',
          )
          enqueueSave()
          return activeId
        },
      }),
      { name: 'zustand/workspace', store: 'workspace', enabled: import.meta.env.DEV },
    ),
  )

  // ─── Hydrate from a doc (shared by sync cache path and async IDB path) ───

  /**
   * A fixed-shape layout's SIZES from a saved doc. The preset owns which panels
   * exist and how they nest; the user owns how big they are. Divider positions
   * are taken only when the saved tree has the preset's exact structure
   * (mosaicNodesEqual ignores splitPercentage); grid cells take their saved
   * position and size only when the saved grid has exactly the preset's cards.
   */
  function fixedShapeSizes(
    doc: WorkspaceDocument,
  ): Partial<Pick<LayoutStoreState, 'mosaicTree' | 'gridLayout'>> {
    const out: Partial<Pick<LayoutStoreState, 'mosaicTree' | 'gridLayout'>> = {}
    const presetTree = defaults.mosaicTree ?? null
    const savedTree = (doc.mosaicTree as MosaicNode<string> | null | undefined) ?? null
    if (presetTree && savedTree && mosaicNodesEqual(presetTree, savedTree)) {
      out.mosaicTree = savedTree
    }
    // Grid geometry only from a saved grid with EXACTLY the preset's cards. A
    // different set is an older preset or a save from a mount that never saw
    // the preset (Overview after a reload, 2026-09-25), not the user's sizing
    // of this one; mixing its cells in would squash the layout.
    // A fixed grid (gridFixed) has no user sizing to keep: always the preset's.
    const presetGrid = defaults.gridLayout ?? []
    const savedGrid = doc.gridLayout ?? []
    const sameCards =
      !defaults.gridFixed &&
      presetGrid.length > 0 &&
      savedGrid.length === presetGrid.length &&
      presetGrid.every((cell) => savedGrid.some((c) => c.i === cell.i))
    if (sameCards) {
      const saved = new Map(savedGrid.map((cell) => [cell.i, cell]))
      out.gridLayout = presetGrid.map((cell) => {
        const mine = saved.get(cell.i)!
        return { ...cell, x: mine.x, y: mine.y, w: mine.w, h: mine.h }
      })
    }
    return out
  }

  function hydrateFromDoc(doc: WorkspaceDocument, actionName: string) {
    if (fixedShape) {
      // For fixed-shape (preset) layouts: layout structure always comes from
      // the static defaults (preset JSON). Only restore panel states from the
      // doc (IDB or server) for panels that still exist in the current preset.
      const currentPanelIds = new Set(Object.keys(defaults.windowConfigs))
      // Seed with the preset-provided default panel states; persisted user
      // states are merged ON TOP per key below, so user tweaks win but
      // preset defaults fill any keys the stored state doesn't carry.
      const panelStates: Record<string, PanelState | null> = { ...(defaults.panels ?? {}) }
      const panelLoadedMap: Record<string, boolean> = {}

      for (const [panelId, rawState] of Object.entries(doc.panels ?? {})) {
        // Keep a panel if it's a registered preset panel OR a synthetic
        // sub-panel of one (e.g. a ChartGrid cell `panelId::0` or a drill
        // child `panelId::drilled`). Cell/drill ids are never in the static
        // `defaults.windowConfigs`, so without the base-id check their state
        // (per-cell timeframe, chart type, indicators) is dropped on reload.
        const baseId = panelId.replace(/::.*$/, '')
        if (!currentPanelIds.has(panelId) && !currentPanelIds.has(baseId)) continue
        const componentType =
          (defaults.windowConfigs[panelId] ?? defaults.windowConfigs[baseId])?.componentType ??
          'chart'
        const validatedState = validatePanelState(componentType, rawState)
        if (validatedState) {
          panelStates[panelId] = { ...defaults.panels?.[panelId], ...validatedState }
          panelLoadedMap[panelId] = true
        }
      }

      store.setState(
        {
          panels: panelStates,
          panelLoaded: panelLoadedMap,
          globalSettings: (doc.globalSettings as Record<string, unknown>) ?? {},
          ...fixedShapeSizes(doc),
        },
        undefined,
        `${actionName}/localOnly`,
      )
      return true
    }

    const validated = validateLayoutData(
      doc.mosaicTree,
      doc.windowConfigs,
      doc.globalSettings,
      doc.tabGroups,
      doc.gridLayout,
      doc.layoutMode === 'grid',
    )
    if (!validated) return false

    const panelStates: Record<string, PanelState | null> = {}
    const panelLoadedMap: Record<string, boolean> = {}
    for (const [panelId, rawState] of Object.entries(doc.panels ?? {})) {
      // Orphan prune: keep a state only for a live panel or a synthetic
      // sub-panel of one (`base::sfx` ChartGrid cell / drill ids never have
      // their own windowConfig).
      const baseId = panelId.replace(/::.*$/, '')
      const config = validated.windowConfigs[panelId] ?? validated.windowConfigs[baseId]
      if (!config) continue
      const validatedState = validatePanelState(config.componentType, rawState)
      if (validatedState) {
        panelStates[panelId] = validatedState
        panelLoadedMap[panelId] = true
      }
    }

    store.setState(
      {
        mosaicTree: validated.mosaicTree,
        windowConfigs: validated.windowConfigs,
        globalSettings: validated.globalSettings,
        recoveryCount: validated.recoveryCount,
        panels: panelStates,
        panelLoaded: panelLoadedMap,
        tabGroups: validated.tabGroups,
        layoutMode: doc.layoutMode ?? 'mosaic',
        gridLayout: doc.gridLayout ?? [],
      },
      undefined,
      actionName,
    )
    return true
  }

  // ─── Async initialization ───

  async function init() {
    // Stage 1: Read from IDB (fast, ~30ms)
    let doc = await getWorkspaceDoc(workspaceId, layoutId)

    // Try localStorage migration if no IDB entry (one-time migration path)
    if (!doc && !localOnly) {
      const migrated = await migrateFromLocalStorage(workspaceId, layoutId)
      doc = migrated.doc
    }

    if (doc) {
      hydrateFromDoc(doc, 'init/idb')
      if (doc.layoutRevision != null) lastLayoutRevision = doc.layoutRevision
      // Establish the persisted baseline so the first user-driven flush
      // diffs against this initial state.
      lastPersistedSnapshot = snapshotOf(store.getState())
    }

    // Stage 2: Mark layout as loaded (renders the mosaic).
    //
    // Normally we lift the loading gate right after the IDB read. BUT when there
    // is no local doc and this is a server-backed layout (`!localOnly`), the store
    // still holds the DEFAULT placeholder tree (e.g. a single chart) and Stage 3
    // will fetch the real layout from the server — via the controller bootstrap
    // OR the loadLayout fetch (layout switch, first visit to a not-yet-cached
    // layout, e.g. the heatmap page on a slow connection). Lifting now would
    // (1) flash that placeholder before the real layout swaps in, and (2) — worse
    // — let the user interact with it against a null baseline, whose edit can then
    // overwrite the server layout (the LAYOUT-DATA-LOSS-FIX class). So hold the
    // loading screen until Stage 3 reconciliation applies the authoritative
    // layout (runApiReconciliation lifts the gate; a timeout backstops a hang).
    // A layout WITH a local doc lifts instantly (no hold) — cached switches stay
    // snappy; only never-cached layouts show the brief loading screen.
    const holdForServer = !doc && !localOnly
    if (!holdForServer) {
      store.setState({ layoutLoaded: true }, undefined, 'init/loaded')
    }

    // Stage 3: API reconciliation
    //
    // Two paths:
    //   (a) Initial mount with controller bootstrap in flight or just resolved
    //       for THIS layoutId — await it, re-read IDB (which the controller
    //       wrote post-bootstrap), and skip the loadLayout fetch.
    //   (b) Layout switch (no bootstrap promise, or it resolved for a
    //       different layoutId) — fall back to the loadLayout fetch.
    //
    // Both paths converge on the same merge logic.
    if (!localOnly) {
      runApiReconciliation(holdForServer)
    }

    async function runApiReconciliation(holdForServer = false) {
      // When we held the loading screen (holdForServer), lift it once
      // reconciliation resolves — via any exit path — so the mosaic reveals the
      // authoritative layout, not the placeholder. Idempotent.
      const liftLoadingGate = () => {
        if (reconcileLiftTimer) {
          clearTimeout(reconcileLiftTimer)
          reconcileLiftTimer = undefined
        }
        if (!store.getState().layoutLoaded) {
          store.setState({ layoutLoaded: true }, undefined, 'init/loaded')
        }
        // Reconciliation settled (answer, no copy, or failure): an edit held
        // since an early reveal can save now, merged with what arrived.
        if (awaitingServer) {
          awaitingServer = false
          earlyRevealBaseline = null
          if (dirty) enqueueSave()
        }
      }
      if (holdForServer) {
        // Never hang on the loading screen if reconciliation stalls. A fixed
        // shape has its layout in code, so it only waits a short grace period.
        reconcileLiftTimer = setTimeout(
          () => {
            reconcileLiftTimer = undefined
            if (!store.getState().layoutLoaded) {
              if (fixedShape) {
                const s = store.getState()
                awaitingServer = true
                earlyRevealBaseline = {
                  panels: { ...s.panels },
                  globalSettings: { ...s.globalSettings },
                }
              }
              store.setState({ layoutLoaded: true }, undefined, 'init/loaded-timeout')
            }
          },
          config.reconcileTimeoutMs ??
            (fixedShape ? FIXED_SHAPE_REVEAL_GRACE_MS : LOAD_RECONCILE_TIMEOUT_MS),
        )
      }

      // Apply the server payload UNLESS the store holds real, user-edited content
      // against an established baseline. When we never had a local doc
      // (`lastPersistedSnapshot == null`), any "dirty" edit was made against the
      // placeholder default and must yield to the authoritative layout — else a
      // mid-load edit persists the placeholder over the real layout (the
      // LAYOUT-DATA-LOSS-FIX class). With holdForBootstrap the store can't be
      // dirty at all (the gate blocks interaction), so this always applies.
      const shouldApplyOverLocal = () =>
        (!dirty && !saveQueue.isBusy()) || lastPersistedSnapshot == null

      let bootstrapCovered = false
      if (config.bootstrapPromise) {
        try {
          const data = await config.bootstrapPromise
          if (data?.activeLayoutId === layoutId) {
            // Controller bootstrap covered our layoutId — re-read IDB for
            // the freshly-written content and apply via the merge path.
            const freshDoc = await getWorkspaceDoc(workspaceId, layoutId)
            if (freshDoc) {
              if (freshDoc.layoutRevision != null) lastLayoutRevision = freshDoc.layoutRevision
              if (shouldApplyOverLocal()) {
                applyApiPayload({
                  mosaicTree: freshDoc.mosaicTree,
                  windowConfigs: freshDoc.windowConfigs,
                  globalSettings: freshDoc.globalSettings,
                  panels: freshDoc.panels,
                  tabGroups: freshDoc.tabGroups,
                  // Mode + structure travel together — omitting these would
                  // make applyApiPayload treat a grid doc as mosaic.
                  layoutMode: freshDoc.layoutMode,
                  gridLayout: freshDoc.gridLayout,
                  layoutRevision: freshDoc.layoutRevision,
                })
              }
            }
            bootstrapCovered = true
          }
        } catch {
          /* fall through to loadLayout fetch below */
        }
      }

      if (bootstrapCovered) {
        liftLoadingGate()
        return
      }

      // Layout switch path — bootstrap promise didn't cover us. Fetch.
      // Gated on any pending creation of this layout: a GET racing ahead
      // of the create returns null and leaves lastLayoutRevision unset.
      waitForPendingCreate(layoutId)
        .then(() => layoutApi.loadLayout(scope, layoutId))
        .then((data) => {
          // Grid (scroll) layouts have no mosaic tree — still a valid payload.
          if (!data?.mosaicTree && data?.layoutMode !== 'grid') return
          if (data.layoutRevision != null) lastLayoutRevision = data.layoutRevision
          if (!shouldApplyOverLocal()) return
          applyApiPayload(data)
        })
        .catch((err) => {
          console.error('[workspace] Failed to load layout panels:', err)
        })
        .finally(() => {
          liftLoadingGate()
        })
    }

    function applyApiPayload(data: {
      mosaicTree: unknown
      windowConfigs: Record<string, unknown>
      globalSettings?: Record<string, unknown>
      panels?: Record<string, Record<string, unknown>>
      tabGroups?: Record<string, TabGroup>
      layoutMode?: LayoutMode
      gridLayout?: GridCell[]
      layoutRevision?: number
    }) {
      if (fixedShape) {
        applyFixedPayload(data, 'init/api')
        setWorkspaceDoc(
          workspaceId,
          layoutId,
          assembleDocument(store.getState(), lastLayoutRevision),
        )
        return
      }
      // Grid (scroll) layouts legitimately have no mosaic tree — don't bail.
      if (!data?.mosaicTree && data.layoutMode !== 'grid') return

      const validated = validateLayoutData(
        data.mosaicTree,
        data.windowConfigs,
        data.globalSettings ?? {},
        data.tabGroups,
        data.gridLayout,
        data.layoutMode === 'grid',
      )
      if (!validated) return

      // Validate panel states from the payload. Orphan prune: keep a state
      // only for a live panel or a `base::sfx` synthetic sub-panel of one.
      const apiPanelStates: Record<string, PanelState | null> = {}
      if (data.panels) {
        for (const [panelId, rawState] of Object.entries(data.panels)) {
          const baseId = panelId.replace(/::.*$/, '')
          const config = validated.windowConfigs[panelId] ?? validated.windowConfigs[baseId]
          if (!config) continue
          const validatedState = validatePanelState(config.componentType, rawState)
          if (validatedState) {
            apiPanelStates[panelId] = validatedState
          }
        }
      }

      // If IDB already has the same layout structure, skip setState to
      // avoid a re-render cascade. This is the common case — IDB and the
      // payload agree because the user hasn't edited from another tab.
      const currentState = store.getState()
      const apiLeaves = new Set(collectLeaves(validated.mosaicTree))
      const localLeaves = new Set(collectLeaves(currentState.mosaicTree))
      const sameStructure =
        apiLeaves.size === localLeaves.size && [...apiLeaves].every((id) => localLeaves.has(id))
      const allPanelsLocal = Object.keys(apiPanelStates).every(
        (id) => currentState.panels[id] != null,
      )
      // Payloads WITHOUT tabGroups keep the local groups (legacy docs predate
      // the field); payloads WITH them take the sanitized set.
      const incomingTabGroups = data.tabGroups ? validated.tabGroups : currentState.tabGroups
      const sameTabGroups =
        JSON.stringify(currentState.tabGroups) === JSON.stringify(incomingTabGroups)
      const sameGrid =
        (data.layoutMode ?? 'mosaic') === currentState.layoutMode &&
        JSON.stringify(data.gridLayout ?? []) === JSON.stringify(currentState.gridLayout)

      if (
        sameStructure &&
        allPanelsLocal &&
        validated.recoveryCount === 0 &&
        sameTabGroups &&
        sameGrid
      ) {
        return
      }

      // Payload has different data than IDB — merge it in. Snapshot the
      // pre-overwrite local doc first (recoverable if the payload is bad).
      if (currentState.layoutLoaded) {
        void backupWorkspaceDoc(workspaceId, layoutId, assembleDocument(currentState))
      }
      store.setState(
        (s) => {
          // Preserve local per-panel view state (symbolIndex, linkMode, tickerCleared)
          const mergedConfigs = { ...validated.windowConfigs }
          for (const [panelId, cfg] of Object.entries(mergedConfigs)) {
            const local = s.windowConfigs[panelId]
            if (local) {
              mergedConfigs[panelId] = {
                ...cfg,
                symbolIndex: local.symbolIndex,
                tickerCleared: local.tickerCleared,
                linkMode: local.linkMode,
              }
            }
          }

          // Only hydrate panels that don't exist locally
          const mergedPanels = { ...s.panels }
          const mergedPanelLoaded = { ...s.panelLoaded }
          for (const [panelId, state] of Object.entries(apiPanelStates)) {
            if (!s.panels[panelId]) {
              mergedPanels[panelId] = state
              mergedPanelLoaded[panelId] = true
            }
          }

          return {
            mosaicTree: validated.mosaicTree,
            windowConfigs: mergedConfigs,
            globalSettings:
              validated.globalSettings && Object.keys(validated.globalSettings).length > 0
                ? { ...s.globalSettings, ...validated.globalSettings }
                : s.globalSettings,
            recoveryCount: s.recoveryCount + validated.recoveryCount,
            panels: mergedPanels,
            panelLoaded: mergedPanelLoaded,
            tabGroups: data.tabGroups ? validated.tabGroups : s.tabGroups,
            // Mode + structure are ONE atomic unit from the payload — mirror
            // hydrateFromExternal. Falling back to LOCAL mode here (`??
            // s.layoutMode`) would blend a mosaic payload's tree/configs with a
            // local grid's mode+cells, persisting a hybrid doc whose cells
            // reference configs that no longer exist (validated data loss).
            layoutMode: data.layoutMode ?? 'mosaic',
            gridLayout: data.gridLayout ?? [],
          }
        },
        undefined,
        'init/api',
      )
      setWorkspaceDoc(workspaceId, layoutId, assembleDocument(store.getState(), lastLayoutRevision))
      // Update the diff baseline now that we've applied the server's payload.
      lastPersistedSnapshot = snapshotOf(store.getState())
    }

    // Bail if the store was destroyed while init was suspended above. Anything
    // registered past this point would outlive its own teardown.
    if (destroyed) return

    // Set up BroadcastChannel for cross-tab sync (skip for localOnly/preset layouts)
    if (localOnly) {
      bcastLog('open SKIP — localOnly layout', { layoutId })
    } else if (typeof BroadcastChannel === 'undefined') {
      bcastLog('open SKIP — BroadcastChannel undefined', { layoutId })
    } else {
      const channelName = `edge-workspace:${workspaceId}:${layoutId}`
      broadcastChannel = new BroadcastChannel(channelName)
      bcastLog('open', { channel: channelName })
      broadcastChannel.onmessage = async (event) => {
        const isStale = store.getState().isStale
        bcastLog('inbound', {
          channel: channelName,
          data: event.data,
          localRevision: lastLayoutRevision,
          dirty,
          queueBusy: saveQueue.isBusy(),
          isStale,
        })
        if (isStale) {
          // The store is stale — local in-memory state diverges from
          // server. Hydrating now would merge our broken state with the
          // incoming doc and produce a hybrid that exists in NO tab
          // (orphan windowConfigs, panels referenced by ids that aren't
          // in the new mosaic tree, etc.). Ignore broadcasts; user must
          // reload.
          bcastLog('inbound SKIP — store stale (reload required)')
          return
        }
        // Same gate the visibility-resync path applies below, for the same
        // stated reason: hydrateFromExternal clears `dirty` and calls
        // saveQueue.clear(), so hydrating over unsaved work silently discards
        // this tab's structural edits. These two values were already being
        // logged here; they just were not being acted on.
        if (dirty || saveQueue.isBusy()) {
          bcastLog('inbound SKIP — local unsaved work (dirty or queue busy)')
          return
        }
        // Another tab wrote to IDB — re-read and hydrate without triggering a save.
        const freshDoc = await getWorkspaceDoc(workspaceId, layoutId)
        if (!freshDoc) {
          bcastLog('inbound SKIP hydrate — IDB has no doc', { layoutId })
          return
        }
        bcastLog('inbound → hydrateFromExternal', {
          layoutId,
          incomingRevision: event.data?.layoutRevision,
          docRevision: freshDoc.layoutRevision,
        })
        hydrateFromExternal(freshDoc, event.data?.layoutRevision)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  // ─── Best-effort flush on tab close / destroy ───
  //
  // beforeunload can't await — fire-and-forget. We bypass the queue here:
  // if a save is already in flight, this duplicates it, which is fine
  // (server is idempotent for last-writer-wins on the doc, and revisioned
  // 409s are tolerable on tab close). IDB is written so the next mount
  // sees what the user typed even if the network call is dropped.

  function flushDirtyState() {
    if (!dirty) return
    // Never flush default (un-hydrated) state — see the enqueueSave guard. With
    // that guard `dirty` shouldn't be set pre-hydration, but this is a
    // defense-in-depth backstop on the destroy/beforeunload path.
    if (!store.getState().layoutLoaded) return
    // Stale means we could not reach the server. enqueueSave refuses to push
    // in this state; this path must respect the same gate or destroy() and
    // tab-hide punch straight through it. Reachable whenever a save is in
    // flight (which clears `dirty` at task start) and a second edit re-marks
    // dirty before the connection drops.
    if (store.getState().isStale) return

    const state = store.getState()
    const doc = assembleDocument(state, lastLayoutRevision)
    setWorkspaceDoc(workspaceId, layoutId, doc)

    if (!localOnly && !awaitingServer) {
      const nextSnapshot = snapshotOf(state)
      const revisioned =
        !fixedShape && isRevisionedLayoutChange(lastPersistedSnapshot, nextSnapshot)
      layoutApi
        .saveLayout(
          scope,
          layoutId,
          {
            // Keep-both: persist BOTH structures (see assembleDocument).
            mosaicTree: state.mosaicTree,
            windowConfigs: state.windowConfigs,
            globalSettings: state.globalSettings,
            panels: filterNullPanels(state.panels),
            // ALWAYS send layoutMode + tabGroups, even when 'mosaic' / empty.
            // `save_layout` builds a partial DynamoDB UpdateExpression that SETs
            // only the keys present in the body and issues no REMOVE, so an
            // omitted key keeps its stored value forever. Omitting these two on
            // the clearing edge made grid->mosaic conversion and last-tab-close
            // silently revert on the next bootstrap. See store.unit.test.ts
            // 'always sends layoutMode + tabGroups'.
            tabGroups: state.tabGroups,
            layoutMode: state.layoutMode,
            // gridLayout stays CONDITIONAL on purpose. Keep-both (see
            // assembleDocument) says the inactive structure rides along as a
            // disaster-recovery fallback and is never destroyed, so here
            // omission is the safe direction: it preserves the server's copy.
            // Force-sending [] would delete a fallback we may still need.
            //
            // KNOWN LIMITATION, deliberate: because the backend never issues a
            // REMOVE, there is consequently no way to intentionally CLEAR a
            // stored gridLayout from here. No UI path needs that today. If one
            // ever does, add an explicit clear rather than making this
            // unconditional, which would destroy the fallback on every mosaic
            // save.
            ...(state.gridLayout.length > 0 ? { gridLayout: state.gridLayout } : {}),
            layoutRevision: lastLayoutRevision,
            ...(config.getLayoutName?.() != null ? { name: config.getLayoutName()! } : {}),
          },
          { revisioned },
        )
        .then((result) => {
          // Same rule as the queue path: only revisioned responses carry an
          // authoritative layoutRevision.
          if (revisioned && result?.layoutRevision != null) {
            lastLayoutRevision = result.layoutRevision
          }
          lastPersistedSnapshot = nextSnapshot
          // Only now is the change actually on the server.
          dirty = false
        })
        .catch((err) => {
          // Leave `dirty` set. Clearing it before the await (as this used to)
          // meant a rejected save silently dropped the retry signal: the edit
          // survived only in IDB, and the next mount's applyApiPayload could
          // revert it from the stale server copy. Keeping it dirty lets a
          // later flush or enqueueSave try again.
          console.error('[workspace] Failed to save layout:', err)
        })
    } else {
      // localOnly: IDB IS the persistence target, and it was written above,
      // so the change is durable and no longer dirty.
      dirty = false
    }
  }

  // Flush pending changes before page unload (tab close / refresh)
  function handleBeforeUnload() {
    flushDirtyState()
  }
  window.addEventListener('beforeunload', handleBeforeUnload)

  // Re-sync from API when tab regains focus (catches cross-browser drift)
  function handleVisibilityChange() {
    if (document.hidden) {
      lastHiddenAt = Date.now()
      // Flush pending changes immediately so other tabs/browsers see them
      if (dirty) flushDirtyState()
      return
    }
    if (localOnly) return
    // Skip if hidden for less than 5 seconds (avoids re-fetching on quick tab switches)
    if (Date.now() - lastHiddenAt < 5000) return
    // Skip if the store is stale — see broadcast onmessage gate for details.
    if (store.getState().isStale) return
    // Skip if a save is in-flight or we have unsaved changes
    if (dirty || saveQueue.isBusy()) return

    layoutApi
      .loadLayout(scope, layoutId)
      .then((data) => {
        // Grid (scroll) layouts have no mosaic tree — still a valid payload.
        if (!data?.mosaicTree && data?.layoutMode !== 'grid') return
        // Only hydrate if server has a newer layoutRevision
        if (
          lastLayoutRevision != null &&
          data.layoutRevision != null &&
          data.layoutRevision <= lastLayoutRevision
        )
          return
        // Re-check: user may have made changes while the fetch was in-flight.
        // hydrateFromExternal drops pending saves, so we'd lose those changes.
        if (dirty || saveQueue.isBusy()) return

        hydrateFromExternal(data, data.layoutRevision)
        setWorkspaceDoc(
          workspaceId,
          layoutId,
          assembleDocument(store.getState(), lastLayoutRevision),
        )
      })
      .catch((err) => {
        console.error('[workspace] Failed to reload layout:', err)
      })
  }

  // ─── Cleanup function ───

  function destroy() {
    destroyed = true
    saveQueue.clear()
    if (reconcileLiftTimer) {
      clearTimeout(reconcileLiftTimer)
      reconcileLiftTimer = undefined
    }
    if (deferRetryTimer) {
      clearTimeout(deferRetryTimer)
      deferRetryTimer = null
    }
    window.removeEventListener('beforeunload', handleBeforeUnload)
    document.removeEventListener('visibilitychange', handleVisibilityChange)

    // Close BroadcastChannel
    if (broadcastChannel) {
      bcastLog('close', { layoutId })
      broadcastChannel.close()
      broadcastChannel = null
    }

    flushDirtyState()
  }

  // Start initialization
  init()

  return {
    store,
    destroy,
  }
}
